/**
 * 保留トレイ (deal_merge_candidates) の hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.3
 */
import type {
  DealMergeCandidate,
  MergeCandidateAction,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface DealMergeCandidateRecord extends DealMergeCandidate {
  id: string;
}

interface UseCandidatesOptions {
  /** true なら resolved 済みも含める。default: false (未解決のみ) */
  includeResolved?: boolean;
}

export function useDealMergeCandidates(options?: UseCandidatesOptions) {
  return useQuery({
    queryKey: ['deal_merge_candidates', options ?? null],
    queryFn: async (): Promise<DealMergeCandidateRecord[]> => {
      const constraints = [];
      if (!options?.includeResolved) {
        constraints.push(where('resolvedAt', '==', null));
      }
      constraints.push(orderBy('createdAt', 'desc'));
      const q = query(collection(db, 'deal_merge_candidates'), ...constraints);
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as DealMergeCandidate),
      }));
    },
  });
}

export function useDealMergeCandidate(id: string | null | undefined) {
  return useQuery({
    queryKey: ['deal_merge_candidates', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<DealMergeCandidateRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'deal_merge_candidates', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as DealMergeCandidate) };
    },
  });
}

const resolveMergeCandidateFn = httpsCallable<
  {
    candidateId: string;
    action: MergeCandidateAction;
    targetDealId?: string;
  },
  { candidateId: string; action: MergeCandidateAction; resolvedDealId: string | null }
>(functions, 'resolveMergeCandidate');

export function useResolveMergeCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      candidateId: string;
      action: MergeCandidateAction;
      targetDealId?: string;
    }) => {
      const res = await resolveMergeCandidateFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deal_merge_candidates'] });
      qc.invalidateQueries({
        queryKey: ['deal_merge_candidates', 'detail', vars.candidateId],
      });
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
